import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, User, Cpu, UserFocus, Hospital, Heartbeat } from "@phosphor-icons/react";
import HeroMap from "./HeroMap";

const FLOW = [
    { icon: User, label: "Patient" },
    { icon: Cpu, label: "AI" },
    { icon: UserFocus, label: "Specialist" },
    { icon: Hospital, label: "Hospital" },
    { icon: Heartbeat, label: "Recovery" },
];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const mapY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-gradient-to-b from-tint-blue via-white to-white">
            <div
                className="absolute inset-0 opacity-[0.35]"
                style={{ backgroundImage: "radial-gradient(#CBD5E1 1px, transparent 1px)", backgroundSize: "32px 32px" }}
                aria-hidden="true"
            />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-36 lg:pt-44 pb-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-6">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                            className="inline-flex items-center gap-2 rounded-full border border-brand-green/25 bg-tint-green px-4 py-1.5"
                            data-testid="hero-eyebrow"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-brand-green" />
                            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-green">The Future of Cross-Border Healthcare</span>
                        </motion.div>

                        <h1 className="mt-8 font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02] text-brand-blue" data-testid="hero-headline">
                            <MaskedLine delay={0.15}>Healthcare</MaskedLine>
                            <MaskedLine delay={0.32}>
                                <span className="text-brand-green">Without Borders.</span>
                            </MaskedLine>
                        </h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-6 font-heading text-xl sm:text-2xl font-medium text-slate-700"
                            data-testid="hero-subheadline"
                        >
                            One intelligent journey from diagnosis to recovery.
                        </motion.p>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-slate-600"
                            data-testid="hero-body"
                        >
                            GoMedVoyage is building an AI-powered cross-border healthcare platform connecting international
                            patients with trusted healthcare providers in India — simplifying medical navigation, treatment
                            coordination, travel, and post-treatment care.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-10 flex flex-wrap items-center gap-4"
                        >
                            <button
                                data-testid="hero-explore-btn"
                                onClick={() => document.querySelector("#why-gomedvoyage") && (window.__lenis ? window.__lenis.scrollTo("#why-gomedvoyage", { offset: -90, duration: 1.4 }) : null)}
                                className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-brand-blue/25 hover:bg-brand-blue-light hover:shadow-xl active:scale-95 transition-[background-color,box-shadow,transform] duration-200"
                            >
                                Explore GoMedVoyage
                                <ArrowDown size={16} weight="bold" />
                            </button>
                            <Link
                                data-testid="hero-partner-btn"
                                to="/partner"
                                className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-8 py-4 text-sm font-semibold text-brand-blue hover:bg-slate-50 hover:border-brand-green/40 active:scale-95 transition-[background-color,border-color,transform] duration-200"
                            >
                                Partner With Us
                                <ArrowRight size={16} weight="bold" />
                            </Link>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1, delay: 1.3 }}
                            className="mt-8 text-sm text-slate-500 flex items-center gap-3"
                            data-testid="hero-supporting-statement"
                        >
                            <span className="h-px w-10 bg-brand-green" />
                            Building from India. Connecting the world to better healthcare.
                        </motion.p>
                    </div>

                    <motion.div style={{ y: mapY, opacity: fade }} className="lg:col-span-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.4 }}>
                        <HeroMap />
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 2.4 }}
                            className="mt-2 flex flex-wrap items-center justify-center gap-2"
                            data-testid="hero-journey-flow"
                        >
                            {FLOW.map((step, i) => (
                                <span key={step.label} className="flex items-center gap-2">
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-brand-blue shadow-sm">
                                        <step.icon size={14} weight="duotone" className="text-brand-green" />
                                        {step.label}
                                    </span>
                                    {i < FLOW.length - 1 && <ArrowRight size={12} className="text-slate-400" />}
                                </span>
                            ))}
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
